/**
 * Чем сейчас держится свежесть экрана ревизии (§3.8).
 *
 * ## Говорит только деградация
 *
 * Различать нужно ровно один момент — когда экран перестал обновляться сам.
 * Поток и опрос дают внешне одинаковый результат, но по-разному ведут себя при
 * отказе: экран, который потерял поток и молча перешёл на опрос раз в пять
 * секунд, неотличим от экрана, который потерял и то и другое, а человек, ждущий
 * окончания распознавания, обязан видеть разницу между «обновляется сейчас» и
 * «обновится, когда я нажму».
 *
 * Отсюда не следует, что о живом потоке надо докладывать. Зелёная плашка в
 * шапке горела всё время нормальной работы и не меняла ни одного решения: она
 * сообщала «всё как обычно» тому, кто и так это видит по обновляющимся цифрам.
 * Поэтому индикатор молчит, пока свежесть держит поток, и появляется, когда её
 * держит опрос. Молчание читается как «поток жив», и это честно ровно до тех
 * пор, пока деградация остаётся громкой, — то есть пока отсюда виден каждый
 * режим, кроме живого.
 *
 * Состояние названо словом, а не только цветом: различение исключительно цветом
 * не проходит §17.
 */
import { type ReactNode } from 'react';
import { Button, Space, Tooltip } from 'antd';
import { ToneTag, type Tone } from '../../shared/tags.js';
import { useFolderStream } from './stream.js';

/**
 * Состояния, о которых индикатор говорит.
 *
 * Тип сужен намеренно: `live` и `connecting` сюда не входят, и отрендерить их
 * случайно нельзя — замысел «о живом потоке не сообщаем» держится проверкой
 * типов, а не памятью правящего.
 */
type LoudStatus = 'reconnecting' | 'lost';

const LABELS: Record<LoudStatus, string> = {
  reconnecting: 'поток восстанавливается',
  lost: 'поток потерян, идёт опрос',
};

/**
 * Тон нарастает вместе с тяжестью: разрыв, который вот-вот залечится сам, не
 * должен выглядеть как режим, из которого экран сам уже не выйдет.
 */
const TONES: Record<LoudStatus, Tone> = {
  reconnecting: 'warning',
  lost: 'danger',
};

function isLoud(status: string): status is LoudStatus {
  return status === 'reconnecting' || status === 'lost';
}

export function StreamIndicator(): ReactNode {
  const stream = useFolderStream();
  if (stream === null) return null;

  // Локальная константа, а не `stream.status` по месту: сужение типа проверкой
  // `isLoud` должно дожить до `TONES[status]` ниже.
  const status = stream.status;
  // `connecting` молчит вместе с `live`: это доли секунды при монтировании, и
  // плашка, мелькающая на каждом открытии экрана, — тот же шум, только
  // мельтешащий. Залипнуть этот статус не может: первая же неудачная попытка
  // переводит цикл в `reconnecting` (см. `stream.tsx`).
  //
  // «лента обрывалась» — не состояние соединения, а факт неполной истории, и он
  // не перестаёт быть верным оттого, что поток восстановился. Поэтому она
  // остаётся видимой и при живом потоке, одна, без плашки состояния.
  if (!isLoud(status) && !stream.truncated) return null;

  const hint = `Свежесть держится опросом раз в 5 с. ${
    stream.error === null ? '' : `Причина разрыва: ${stream.error}. `
  }Попыток подряд: ${String(stream.attempts)}.`;

  return (
    <Space size={6} wrap>
      {isLoud(status) && (
        <Tooltip title={hint}>
          <span>
            <ToneTag tone={TONES[status]} testId="stream-status">
              {LABELS[status]}
            </ToneTag>
          </span>
        </Tooltip>
      )}
      {stream.truncated && (
        <Tooltip title="Часть ленты событий уже вне окна хранения. Состояние перечитано целиком по REST, но история событий за пропущенный период неполна.">
          <ToneTag tone="warning" testId="stream-truncated">
            лента обрывалась
          </ToneTag>
        </Tooltip>
      )}
      {stream.status === 'lost' && (
        <Button size="small" onClick={stream.reconnect} data-testid="stream-reconnect">
          Подключить заново
        </Button>
      )}
    </Space>
  );
}
